/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: PhonebookFormView.tsx
 🗓️ Created: 2025-12-05
 🧩 Description:
    Form for creating/editing phonebook configurations
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BookOpen, Phone, Users, PhoneCall, Video, Star, Zap, Copy, Check, Search, Plus, Upload, Download, Eye } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { ModuleFormHeader } from '../../components/layouts/ModuleFormHeader';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { Button } from '../../components/ui/button';
import { MultiSelectButton } from '../../components/ui/MultiSelectButton';
import { MultiSelectModal } from '../../components/ui/MultiSelectModal';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import ImportPhonebookContactsModal from './ImportPhonebookContactsModal';
import { PhonebookModulesSection } from './components/PhonebookModulesSection';
import PaginationAdvanced from '../../components/ui/PaginationAdvanced';
import { navigateToPhonebookDuplicate } from './phonebooksActions';
import {
  fetchPhonebookById,
  createPhonebook,
  updatePhonebook,
  deletePhonebook,
  fetchPhonebooks,
  fetchPhonebookEntries,
  fetchExtensionsForPhonebook,
  fetchQueuesForPhonebook,
  fetchRingGroupsForPhonebook,
  fetchConferencesForPhonebook,
  fetchFeatureCodesForPhonebook,
  fetchSpeedDialsForPhonebook,
  exportPhonebookContacts,
  type SelectableItem,
} from './api/phonebooksApi';
import type { PhonebookFormData, Phonebook, PhonebookEntry } from './types';

import PhonebookPreviewModal, { type PreviewContact } from './PhonebookPreviewModal';

const defaultFormData: PhonebookFormData = {
  description: '',
  type: 'internal',
  url: '',
  enabled: true,
  selectedExtensionIds: [],
  selectedQueueIds: [],
  selectedRingGroupIds: [],
  selectedConferenceIds: [],
  selectedFeatureCodeIds: [],
  selectedSpeedDialIds: [],
};

// Helper function to remove port from URL
const removePortFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    // Remove port (phones should access without port, server will detect phone type)
    urlObj.port = '';
    return urlObj.toString().replace(/\/$/, ''); // Remove trailing slash
  } catch (e) {
    // Fallback: use regex to remove port
    // Remove port pattern :PORT from URL
    return url.replace(/:\d+(\/|$)/, '$1').replace(/\/$/, '');
  }
};

// Helper function to get public base URL (without port)
const getPublicBaseUrl = (): string => {
  // Try to get from environment variable first
  const envBaseUrl = import.meta.env.VITE_PUBLIC_BASE_URL;
  if (envBaseUrl) {
    return removePortFromUrl(envBaseUrl);
  }

  // Fallback to API URL or current origin
  let apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
  // Remove /api if present
  apiUrl = apiUrl.replace(/\/api$/, '') || window.location.origin;

  return removePortFromUrl(apiUrl);
};

// Helper function to generate random code (e.g., "cJinsNudN")
const generateRandomCode = (length: number = 10): string => {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Helper function to generate full URL with random code
const generateFullUrlWithRandomCode = (): string => {
  const randomCode = generateRandomCode(10);
  const baseUrl = getPublicBaseUrl();
  return `${baseUrl}/phonebook/${randomCode}`;
};

export default function PhonebookFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const resetFlag = searchParams.get('_reset');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError, showInfo } = useNotifications();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<PhonebookFormData>(defaultFormData);
  const [originalData, setOriginalData] = useState<PhonebookFormData>(defaultFormData);
  const [hasChanges, setHasChanges] = useState(false);
  const [phonebookDetails, setPhonebookDetails] = useState<Phonebook | null>(null);

  // Multi-select modal states
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [extensions, setExtensions] = useState<SelectableItem[]>([]);
  const [queues, setQueues] = useState<SelectableItem[]>([]);
  const [ringGroups, setRingGroups] = useState<SelectableItem[]>([]);
  const [conferences, setConferences] = useState<SelectableItem[]>([]);
  const [featureCodes, setFeatureCodes] = useState<SelectableItem[]>([]);
  const [speedDials, setSpeedDials] = useState<SelectableItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Quick list state
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListPhonebooks, setQuickListPhonebooks] = useState<Phonebook[]>([]);
  const [initialPhonebooks, setInitialPhonebooks] = useState<Phonebook[]>([]);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const quickListLoadingRef = useRef(false);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // External contacts state (PhonebookContact only, not InternalPhonebookEntry)
  const [externalContacts, setExternalContacts] = useState<import('./types').PhonebookContact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Pagination and search for external contacts
  const [contactsSearchQuery, setContactsSearchQuery] = useState('');
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsItemsPerPage, setContactsItemsPerPage] = useState(25);

  const mode = id && id !== 'new' ? 'edit' : 'create';
  const isEditMode = mode === 'edit';

  // Reset form when navigating to new form after "Save and new"
  useEffect(() => {
    if (!isEditMode && !duplicateId && resetFlag) {
      // Clear form data
      const newUrl = generateFullUrlWithRandomCode();
      setFormData({
        ...defaultFormData,
        url: newUrl,
      });
      setOriginalData(defaultFormData);
      setHasChanges(false);

      // Remove reset flag from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('_reset');
      setSearchParams(newSearchParams, { replace: true });

      // Clear sessionStorage reset flag if exists
      try {
        sessionStorage.removeItem('phonebooks:saveAction:reset');
      } catch (error) {
        // Ignore sessionStorage errors
      }
    }
  }, [resetFlag, isEditMode, duplicateId, searchParams, setSearchParams]);

  // Load data in edit mode or duplicate mode
  useEffect(() => {
    if (isEditMode && id) {
      loadPhonebook();
    } else if (duplicateId && !isEditMode) {
      // Load phonebook to duplicate
      loadPhonebookForDuplicate();
    }
  }, [id, isEditMode, duplicateId]);

  // Load external contacts when editing external phonebook
  useEffect(() => {
    if (isEditMode && id && formData.type === 'external') {
      loadExternalContacts();
    } else {
      setExternalContacts([]);
    }
  }, [isEditMode, id, formData.type]);

  // Load module items when type is internal
  useEffect(() => {
    if (formData.type === 'internal') {
      loadModuleItems();
    }
  }, [formData.type]);

  // Auto-generate URL with random code when creating new phonebook (only once)
  useEffect(() => {
    if (!isEditMode && !formData.url && !duplicateId) {
      const generatedUrl = generateFullUrlWithRandomCode();
      setFormData((prev) => ({ ...prev, url: generatedUrl }));
    }
  }, []); // Only run once when component mounts in create mode

  const loadPhonebook = async () => {
    try {
      setLoading(true);
      const phonebook = await fetchPhonebookById(id!);
      setPhonebookDetails(phonebook);
      const formState: PhonebookFormData = {
        description: phonebook.description,
        type: phonebook.type,
        url: phonebook.url,
        enabled: phonebook.enabled,
        selectedExtensionIds: phonebook.selectedExtensionIds || [],
        selectedQueueIds: phonebook.selectedQueueIds || [],
        selectedRingGroupIds: phonebook.selectedRingGroupIds || [],
        selectedConferenceIds: phonebook.selectedConferenceIds || [],
        selectedFeatureCodeIds: phonebook.selectedFeatureCodeIds || [],
        selectedSpeedDialIds: phonebook.selectedSpeedDialIds || [],
      };
      setFormData(formState);
      setOriginalData(formState);
    } catch (error) {
      showError(error instanceof Error ? error.message : t('phonebooks.messages.loadError'));
      navigate('/pbx/applications/phonebooks');
    } finally {
      setLoading(false);
    }
  };

  const loadPhonebookForDuplicate = async () => {
    try {
      setLoading(true);
      const phonebook = await fetchPhonebookById(duplicateId!);
      const formState: PhonebookFormData = {
        description: `${phonebook.description} (Copy)`,
        type: phonebook.type,
        url: generateFullUrlWithRandomCode(), // Generate new random code for duplicate
        enabled: phonebook.enabled,
        selectedExtensionIds: phonebook.selectedExtensionIds || [],
        selectedQueueIds: phonebook.selectedQueueIds || [],
        selectedRingGroupIds: phonebook.selectedRingGroupIds || [],
        selectedConferenceIds: phonebook.selectedConferenceIds || [],
        selectedFeatureCodeIds: phonebook.selectedFeatureCodeIds || [],
        selectedSpeedDialIds: phonebook.selectedSpeedDialIds || [],
      };
      setFormData(formState);
      setOriginalData(defaultFormData); // Original data is empty for duplicate
    } catch (error) {
      showError(error instanceof Error ? error.message : t('phonebooks.messages.loadError'));
      navigate('/pbx/applications/phonebooks');
    } finally {
      setLoading(false);
    }
  };

  // Load external contacts for external phonebooks
  const loadExternalContacts = useCallback(async () => {
    if (!id) return;

    try {
      setLoadingContacts(true);
      const entries = await fetchPhonebookEntries(id);
      // Filter only external contacts - use type guard (PhonebookContact has contactType)
      const external = entries.filter((entry): entry is import('./types').PhonebookContact =>
        'contactType' in entry && entry.contactType === 'external'
      );
      setExternalContacts(external);
    } catch (error) {
      console.error('Failed to load external contacts:', error);
      // Don't show error to user, just log it
    } finally {
      setLoadingContacts(false);
    }
  }, [id]);

  const loadModuleItems = async () => {
    try {
      setLoadingItems(true);
      const [exts, qs, rgs, confs, fcs, sds] = await Promise.all([
        fetchExtensionsForPhonebook(),
        fetchQueuesForPhonebook(),
        fetchRingGroupsForPhonebook(),
        fetchConferencesForPhonebook(),
        fetchFeatureCodesForPhonebook(),
        fetchSpeedDialsForPhonebook(),
      ]);
      setExtensions(exts);
      setQueues(qs);
      setRingGroups(rgs);
      setConferences(confs);
      setFeatureCodes(fcs);
      setSpeedDials(sds);
    } catch (error) {
      console.error('Failed to load module items:', error);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleMultiSelectChange = (field: keyof PhonebookFormData, selectedIds: string[]) => {
    setFormData({
      ...formData,
      [field]: selectedIds.map(id => parseInt(id, 10)),
    });
  };

  // Handle download CSV template
  const handleDownloadCSVTemplate = useCallback(() => {
    // CSV header row
    const headers = ['first_name', 'last_name', 'phone', 'mobile_number', 'home_number', 'organization', 'job_title', 'email'];

    // Example row
    const exampleRow = ['John', 'Doe', '+1234567890', '+0987654321', '', 'Acme Inc', 'Manager', 'john@example.com'];

    // Create CSV content
    const csvContent = [
      headers.join(','),
      exampleRow.join(','),
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'phonebook_template.csv');
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }, []);

  // Handle import CSV - opens modal
  const handleImportCSV = useCallback(() => {
    // Validate required fields if phonebook is not saved yet
    if (!isEditMode || !id) {
      if (!formData.description || !formData.url) {
        showError(t('phonebooks.messages.saveError', 'Please fill in all required fields before importing.'));
        return;
      }
    }
    setShowImportModal(true);
  }, [isEditMode, id, formData, showError, t]);

  // Handle save phonebook for import (called by modal if phonebook doesn't exist)
  const handleSaveForImport = useCallback(async (): Promise<number | null> => {
    try {
      const savedPhonebook = await createPhonebook(formData);
      showSuccess(t('phonebooks.messages.createSuccess'));

      // Update form state
      setOriginalData({ ...formData });
      setHasChanges(false);

      // Update URL to edit mode
      navigate(`/pbx/applications/phonebooks/${savedPhonebook.id}`, { replace: true });

      return savedPhonebook.id;
    } catch (error) {
      showError(error instanceof Error ? error.message : t('phonebooks.messages.saveError'));
      return null;
    }
  }, [formData, navigate, showSuccess, showError, t]);

  // Handle import success
  const handleImportSuccess = useCallback(() => {
    // Reload external contacts after import
    if (isEditMode && id && formData.type === 'external') {
      loadExternalContacts();
    }
  }, [isEditMode, id, formData.type, loadExternalContacts]);

  // Handle export contacts
  const handleExportContacts = useCallback(async () => {
    if (!isEditMode || !id || formData.type !== 'external') return;

    setIsExporting(true);
    try {
      const blob = await exportPhonebookContacts(id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `phonebook-${formData.description || 'contacts'}-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showSuccess(t('phonebooks.messages.exportSuccess', 'Contacts exported successfully'));
    } catch (error) {
      showError(error instanceof Error ? error.message : t('phonebooks.messages.exportError', 'Failed to export contacts'));
    } finally {
      setIsExporting(false);
    }
  }, [isEditMode, id, formData.type, formData.description, showSuccess, showError, t]);

  // Filter and paginate external contacts
  const filteredContacts = useMemo(() => {
    if (!contactsSearchQuery.trim()) {
      return externalContacts;
    }
    const query = contactsSearchQuery.toLowerCase();
    return externalContacts.filter((contact) => {
      const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.toLowerCase();
      const phone = (contact.phoneNumber || '').toLowerCase();
      const mobile = (contact.mobileNumber || '').toLowerCase();
      const organization = (contact.organization || '').toLowerCase();
      const email = (contact.email || '').toLowerCase();

      return (
        fullName.includes(query) ||
        phone.includes(query) ||
        mobile.includes(query) ||
        organization.includes(query) ||
        email.includes(query)
      );
    });
  }, [externalContacts, contactsSearchQuery]);

  // Calculate pagination
  const contactsTotalPages = Math.ceil(filteredContacts.length / contactsItemsPerPage);
  const contactsStartIndex = (contactsPage - 1) * contactsItemsPerPage;
  const contactsEndIndex = contactsStartIndex + contactsItemsPerPage;
  const paginatedContacts = filteredContacts.slice(contactsStartIndex, contactsEndIndex);

  // Reset to first page when search changes or items per page changes
  useEffect(() => {
    setContactsPage(1);
  }, [contactsSearchQuery, contactsItemsPerPage]);

  // Generate sample contacts for preview modal (max 3 per type)
  const previewContacts = useMemo((): PreviewContact[] => {
    const contacts: PreviewContact[] = [];

    if (formData.type === 'internal') {
      // Add from extensions (max 3)
      const selectedExts = extensions.filter(e => formData.selectedExtensionIds.includes(parseInt(e.id, 10)));
      selectedExts.slice(0, 3).forEach((ext, idx) => {
        contacts.push({
          id: idx + 1,
          name: ext.name,
          phoneNumber: ext.id,
          type: 'extension'
        });
      });

      // Add from queues (max 3)
      const selectedQueues = queues.filter(q => formData.selectedQueueIds.includes(parseInt(q.id, 10)));
      selectedQueues.slice(0, 3).forEach((q, idx) => {
        contacts.push({
          id: contacts.length + idx + 1,
          name: q.name,
          phoneNumber: q.id,
          type: 'queue'
        });
      });

      // Add from ring groups (max 3)
      const selectedRGs = ringGroups.filter(rg => formData.selectedRingGroupIds.includes(parseInt(rg.id, 10)));
      selectedRGs.slice(0, 3).forEach((rg, idx) => {
        contacts.push({
          id: contacts.length + idx + 1,
          name: rg.name,
          phoneNumber: rg.id,
          type: 'ring_group'
        });
      });
    } else {
      // External phonebook - use external contacts (max 3)
      externalContacts.slice(0, 3).forEach((contact, idx) => {
        contacts.push({
          id: idx + 1,
          name: `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unknown',
          phoneNumber: contact.phoneNumber,
          mobileNumber: contact.mobileNumber,
          organization: contact.organization,
          email: contact.email,
          type: 'external'
        });
      });
    }

    return contacts;
  }, [formData.type, formData.selectedExtensionIds, formData.selectedQueueIds, formData.selectedRingGroupIds, extensions, queues, ringGroups, externalContacts]);

  // Handle copy URL to clipboard
  const handleCopyUrl = async () => {
    if (!formData.url) return;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(formData.url);
        setUrlCopied(true);
        setTimeout(() => setUrlCopied(false), 2000);
        showSuccess(t('phonebooks.messages.urlCopied', 'URL copied to clipboard'));
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = formData.url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          setUrlCopied(true);
          setTimeout(() => setUrlCopied(false), 2000);
          showSuccess(t('phonebooks.messages.urlCopied', 'URL copied to clipboard'));
        }
      }
    } catch (error) {
      console.error('Failed to copy URL:', error);
      showError(t('phonebooks.messages.copyError', 'Failed to copy URL'));
    }
  };

  // Check for changes
  useEffect(() => {
    const changed = JSON.stringify(formData) !== JSON.stringify(originalData);
    setHasChanges(changed);
  }, [formData, originalData]);

  // Handle save
  const handleSave = useCallback(async () => {
    try {
      setLoading(true);
      let savedPhonebook;
      if (isEditMode) {
        // Always send all fields when updating, especially for internal phonebooks
        const updateData: PhonebookFormData = {
          description: formData.description,
          type: formData.type,
          url: formData.url,
          enabled: formData.enabled,
          // Always include all selected item arrays for internal phonebooks
          selectedExtensionIds: formData.type === 'internal' ? formData.selectedExtensionIds : [],
          selectedQueueIds: formData.type === 'internal' ? formData.selectedQueueIds : [],
          selectedRingGroupIds: formData.type === 'internal' ? formData.selectedRingGroupIds : [],
          selectedConferenceIds: formData.type === 'internal' ? formData.selectedConferenceIds : [],
          selectedFeatureCodeIds: formData.type === 'internal' ? formData.selectedFeatureCodeIds : [],
          selectedSpeedDialIds: formData.type === 'internal' ? formData.selectedSpeedDialIds : [],
        };
        savedPhonebook = await updatePhonebook(id!, updateData);
        showSuccess(t('phonebooks.messages.updateSuccess'));
      } else {
        savedPhonebook = await createPhonebook(formData);
        showSuccess(t('phonebooks.messages.createSuccess'));
      }

      setPhonebookDetails(savedPhonebook);
      setOriginalData({ ...formData });
      setHasChanges(false);
      return { id: savedPhonebook.id };
    } catch (error) {
      showError(error instanceof Error ? error.message : t('phonebooks.messages.saveError'));
      return undefined;
    } finally {
      setLoading(false);
    }
  }, [formData, isEditMode, id, hasChanges, showSuccess, showError, showInfo, t]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSave,
    listPath: '/pbx/applications/phonebooks',
    newPath: '/pbx/applications/phonebooks/new',
    mode,
    getEditPath: (savedData) => `/pbx/applications/phonebooks/${savedData.id}`,
    storageKey: 'phonebooks:saveAction',
  });

  // Handle cancel
  const handleCancel = useCallback(() => {
    setFormData({ ...originalData });
    setHasChanges(false);
  }, [originalData]);

  // Delete handlers
  const handleDeleteClick = useCallback(() => {
    if (!isEditMode || !id) return;
    setShowDeleteModal(true);
  }, [isEditMode, id]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !id) return;

    setIsDeleting(true);
    try {
      await deletePhonebook(id);
      showSuccess(t('phonebooks.messages.deleteSuccess'));
      navigate('/pbx/applications/phonebooks');
    } catch (error) {
      showError(error instanceof Error ? error.message : t('phonebooks.messages.deleteError'));
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [isEditMode, id, navigate, showSuccess, showError, t]);

  // Duplicate handler
  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToPhonebookDuplicate(navigate, id);
  }, [isEditMode, id, navigate]);

  // Quick list handlers
  const loadInitialPhonebooks = useCallback(async () => {
    if (quickListLoadingRef.current) return;

    quickListLoadingRef.current = true;
    setIsLoadingInitial(true);
    try {
      const result = await fetchPhonebooks({
        page: 1,
        limit: 100,
        sortBy: 'description',
        sortOrder: 'asc',
      });
      setInitialPhonebooks(result.data);
      setQuickListPhonebooks(result.data);
    } catch (error) {
      setInitialPhonebooks([]);
      setQuickListPhonebooks([]);
    } finally {
      setIsLoadingInitial(false);
      quickListLoadingRef.current = false;
    }
  }, []);

  const loadQuickListPhonebooks = useCallback(async (search: string) => {
    const normalizedSearch = search.trim();
    if (normalizedSearch && normalizedSearch.length < 2) {
      setQuickListPhonebooks(initialPhonebooks);
      return;
    }

    setIsSearching(true);
    try {
      const result = await fetchPhonebooks({
        page: 1,
        limit: 50,
        sortBy: 'description',
        sortOrder: 'asc',
        search: normalizedSearch,
      });
      setQuickListPhonebooks(result.data);
    } catch (error) {
      setQuickListPhonebooks([]);
    } finally {
      setIsSearching(false);
    }
  }, [initialPhonebooks]);

  // Load initial phonebooks when modal opens
  useEffect(() => {
    if (showQuickList && initialPhonebooks.length === 0 && !quickListLoadingRef.current) {
      void loadInitialPhonebooks();
    }
  }, [showQuickList, initialPhonebooks.length, loadInitialPhonebooks]);

  // Debounced search for quick list
  useEffect(() => {
    if (!showQuickList) {
      setQuickListSearch('');
      return;
    }

    const normalizedSearch = quickListSearch.trim();
    if (!normalizedSearch || normalizedSearch.length < 2) {
      setQuickListPhonebooks(initialPhonebooks);
      return;
    }

    const timer = setTimeout(() => {
      loadQuickListPhonebooks(quickListSearch);
    }, 800);

    return () => clearTimeout(timer);
  }, [quickListSearch, showQuickList, loadQuickListPhonebooks, initialPhonebooks]);

  // Navigate to list
  const handleGoToList = useCallback(() => {
    navigate('/pbx/applications/phonebooks');
  }, [navigate]);

  // Navigate to new phonebook
  const handleAddNew = useCallback(() => {
    navigate('/pbx/applications/phonebooks/new');
  }, [navigate]);

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <ModuleFormHeader
        icon={<BookOpen className="h-5 w-5" />}
        title={isEditMode ? `${t('phonebooks.editPhonebook')} - ${formData.description || ''}` : t('phonebooks.createPhonebook')}
        backButtonText={t('phonebooks.actions.backToList')}
        onBack={handleGoToList}
        actions={[
          {
            icon: <Eye className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('phonebooks.actions.preview', 'Preview Format'),
            onClick: () => setShowPreviewModal(true),
          },
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('phonebooks.actions.quickList', 'Quick List'),
            onClick: () => setShowQuickList(true),
          },
          ...(formData.type === 'external'
            ? [
              ...(!isEditMode
                ? [
                  {
                    icon: <Download className="h-5 w-5 text-muted-foreground" />,
                    tooltip: t('phonebooks.actions.downloadTemplate', 'Download Template'),
                    onClick: handleDownloadCSVTemplate,
                  },
                ]
                : []),
              {
                icon: <Upload className="h-5 w-5 text-muted-foreground" />,
                tooltip: t('phonebooks.actions.importCSV', 'Import CSV'),
                onClick: handleImportCSV,
              },
              ...(isEditMode
                ? [
                  {
                    icon: <Download className="h-5 w-5 text-muted-foreground" />,
                    tooltip: t('phonebooks.actions.export', 'Export Contacts'),
                    onClick: handleExportContacts,
                  },
                ]
                : []),
            ]
            : []),
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('phonebooks.actions.duplicate', 'Duplicate'),
              onClick: handleDuplicateFromForm
            }]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('phonebooks.actions.addPhonebook'),
            onClick: handleAddNew,
          }
        ]}
      />

      <div className="mt-8 space-y-6">
        {/* General Section */}
        <FormBox title={t('phonebooks.sections.general')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
            {/* Row 1: Description | Input | Type | Input */}
            <SimpleTooltip content={t('phonebooks.tooltips.description')} position="top">
              <Label htmlFor="description" required className="text-left">
                {t('phonebooks.fields.description')}
              </Label>
            </SimpleTooltip>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('phonebooks.placeholders.description')}
            />
            <SimpleTooltip content={t('phonebooks.tooltips.type')} position="top">
              <Label htmlFor="type" required className="text-left">
                {t('phonebooks.fields.type')}
              </Label>
            </SimpleTooltip>
            <select
              id="type"
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as 'internal' | 'external' })}
              className="w-full px-3 py-2 border border-border bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="internal">{t('phonebooks.internal')}</option>
              <option value="external">{t('phonebooks.external')}</option>
            </select>

            {/* Row 2: URL | Input with Copy Button */}
            <SimpleTooltip content={t('phonebooks.tooltips.url')} position="top">
              <Label htmlFor="url" required className="text-left">
                {t('phonebooks.fields.url')}
              </Label>
            </SimpleTooltip>
            <div className="col-span-3">
              <div className="flex items-center gap-2">
                <Input
                  id="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder={t('phonebooks.placeholders.url', 'https://example.com/phonebook/sales')}
                  disabled={!isEditMode} // URL is auto-generated on create, editable on edit
                  className="flex-1"
                />
                {formData.url && (
                  <SimpleTooltip content={urlCopied ? t('common.copied', 'Copied!') : t('phonebooks.actions.copyUrl', 'Copy URL')} position="top">
                    <button
                      type="button"
                      onClick={handleCopyUrl}
                      className={`p-2 rounded-md transition-colors flex-shrink-0 ${urlCopied
                        ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-background border border-border hover:bg-accent text-muted-foreground hover:text-foreground'
                        }`}
                    >
                      {urlCopied ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                  </SimpleTooltip>
                )}
              </div>
              {!isEditMode && (
                <div className="text-xs text-muted-foreground mt-1 space-y-1">
                  <p>{t('phonebooks.urlAutoGenerated', 'URL will be auto-generated with a random code. You can copy it to configure in your phone.')}</p>
                  <p className="font-medium">{t('phonebooks.urlFormat', 'Format')}: <code className="text-xs bg-muted px-1 py-0.5 rounded">http://your-server/phonebook/{'{'}code{'}'}</code></p>
                  <p>{t('phonebooks.urlCompatible', 'This URL is compatible with all supported phone brands (Yealink, Grandstream, Polycom, Cisco, and 20+ more). The phone will automatically receive the correct format based on its model.')}</p>
                </div>
              )}
            </div>
          </div>
        </FormBox>

        {/* Internal Phonebook Configuration - Module Selectors */}
        {formData.type === 'internal' && (
          <PhonebookModulesSection
            formData={formData}
            extensions={extensions}
            queues={queues}
            ringGroups={ringGroups}
            conferences={conferences}
            featureCodes={featureCodes}
            speedDials={speedDials}
            loadingItems={loadingItems}
            openModal={openModal}
            setOpenModal={setOpenModal}
            onMultiSelectChange={handleMultiSelectChange}
            t={t}
          />
        )}



        {/* External Contacts List - Only for external phonebooks in edit mode */}
        {isEditMode && id && formData.type === 'external' && (
          <FormBox title={t('phonebooks.sections.contacts', 'Imported Contacts')}>
            {loadingContacts ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</div>
              </div>
            ) : externalContacts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">{t('phonebooks.noContactsImported', 'No contacts imported yet.')}</p>
                <p className="text-xs mt-1">{t('phonebooks.useImportButton', 'Use the Import CSV button above to import contacts.')}</p>
              </div>
            ) : (
              <>
                {/* Search and Items Per Page */}
                <div className="flex items-center justify-between gap-4 mb-4">
                  <div className="flex-1 max-w-md">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder={t('phonebooks.searchContacts', 'Search contacts...')}
                        value={contactsSearchQuery}
                        onChange={(e) => setContactsSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-muted-foreground whitespace-nowrap">
                      {t('common.itemsPerPage', 'Items per page')}:
                    </label>
                    <select
                      value={contactsItemsPerPage}
                      onChange={(e) => setContactsItemsPerPage(Number(e.target.value))}
                      className="px-3 py-1.5 text-sm border border-border bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                {/* Filtered and Paginated Contacts */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-3 text-sm font-medium text-foreground">{t('phonebooks.columns.name', 'Name')}</th>
                        <th className="text-left py-2 px-3 text-sm font-medium text-foreground">{t('phonebooks.columns.number', 'Phone')}</th>
                        <th className="text-left py-2 px-3 text-sm font-medium text-foreground">{t('phonebooks.columns.mobile', 'Mobile')}</th>
                        <th className="text-left py-2 px-3 text-sm font-medium text-foreground">{t('phonebooks.columns.organization', 'Organization')}</th>
                        <th className="text-left py-2 px-3 text-sm font-medium text-foreground">{t('phonebooks.columns.email', 'Email')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedContacts.map((contact) => (
                        <tr key={contact.id} className="border-b border-border hover:bg-muted/50">
                          <td className="py-2 px-3 text-sm text-foreground">
                            {contact.firstName || ''} {contact.lastName || ''}
                          </td>
                          <td className="py-2 px-3 text-sm text-foreground">{contact.phoneNumber || '-'}</td>
                          <td className="py-2 px-3 text-sm text-foreground">{contact.mobileNumber || '-'}</td>
                          <td className="py-2 px-3 text-sm text-foreground">{contact.organization || '-'}</td>
                          <td className="py-2 px-3 text-sm text-foreground">{contact.email || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {contactsTotalPages > 1 && (
                  <div className="mt-4">
                    <PaginationAdvanced
                      currentPage={contactsPage}
                      totalPages={contactsTotalPages}
                      onPageChange={setContactsPage}
                      totalItems={filteredContacts.length}
                      itemsPerPage={contactsItemsPerPage}
                      translations={{
                        showing: t('common.showing', 'Showing'),
                        to: t('common.to', 'to'),
                        of: t('common.of', 'of'),
                        results: t('common.results', 'results'),
                        previous: t('common.previous', 'Previous'),
                        next: t('common.next', 'Next'),
                      }}
                    />
                  </div>
                )}

                {/* Summary */}
                <div className="mt-4 text-xs text-muted-foreground">
                  {contactsSearchQuery ? (
                    <>
                      {t('phonebooks.showingFiltered', 'Showing')} {contactsStartIndex + 1}-{Math.min(contactsEndIndex, filteredContacts.length)} {t('phonebooks.of', 'of')} {filteredContacts.length} {t('phonebooks.filteredResults', 'filtered results')} ({t('phonebooks.totalContacts', 'Total')}: {externalContacts.length})
                    </>
                  ) : (
                    <>
                      {t('phonebooks.totalContacts', 'Total')}: {externalContacts.length}
                    </>
                  )}
                </div>
              </>
            )}
          </FormBox>
        )}
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={handleDeleteClick}
        isEditMode={isEditMode}
        isSaving={loading}
        hasChanges={hasChanges}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete')}
        useSaveDropdown={true}
        saveDropdownStorageKey="phonebooks:saveAction"
        saveDisabled={loading || !formData.description || !formData.url}
        deleteDisabled={isDeleting}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('phonebooks.deleteModal.title')}
        message={t('phonebooks.deleteModal.message', {
          description: formData.description || '',
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('phonebooks.actions.quickList', 'Quick List')}
        items={quickListPhonebooks}
        isLoadingInitial={isLoadingInitial}
        isSearching={isSearching}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(phonebook) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/applications/phonebooks/${phonebook.uuid || phonebook.id}`);
        }}
        currentItemId={id}
        renderItem={(phonebook, isCurrent) => (
          <button
            onClick={() => {
              if (!isCurrent) {
                setShowQuickList(false);
                setQuickListSearch('');
                navigate(`/pbx/applications/phonebooks/${phonebook.uuid || phonebook.id}`);
              }
            }}
            disabled={isCurrent}
            className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'
              }`}
          >
            <div className="flex items-center gap-3">
              <BookOpen className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-foreground">
                    {phonebook.description}
                  </span>
                  {isCurrent && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">
                      {t('phonebooks.current', 'Current')}
                    </span>
                  )}
                </div>
                <div className="text-sm text-muted-foreground truncate">
                  {phonebook.type === 'internal' ? t('phonebooks.internal') : t('phonebooks.external')}
                  {phonebook.contactCount !== undefined && ` • ${phonebook.contactCount} ${t('phonebooks.entries')}`}
                </div>
              </div>
            </div>
          </button>
        )}
        searchPlaceholder={t('phonebooks.placeholders.searchPhonebooks', 'Search phonebooks...')}
        minCharsMessage={t('phonebooks.quickListMinChars', 'Type at least 2 characters')}
        emptyMessage={t('phonebooks.quickListPrompt', 'Start typing to search phonebooks...')}
        noResultsMessage={t('phonebooks.quickListNoResults', 'No phonebooks found')}
        headerIcon={<BookOpen className="h-5 w-5" />}
        minSearchChars={2}
      />

      {/* Import Contacts Modal */}
      {formData.type === 'external' && (
        <ImportPhonebookContactsModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onSuccess={handleImportSuccess}
          phonebookId={isEditMode && phonebookDetails ? phonebookDetails.id : null}
          onSaveFirst={!isEditMode || !id ? handleSaveForImport : undefined}
        />
      )}

      {/* Phonebook Preview Modal */}
      <PhonebookPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        phonebookUrl={formData.url}
        contacts={previewContacts}
      />
    </div>
  );
}
